/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Enum } from '../../../platform/Enum';
import { Point } from '../../../platform/geom/Point';
import { Colors } from '../../../ui';
import { ElementSelector2 } from '../../canvas/ui/ElementSelector2';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { ShapeAlgorithm, ShapePath } from './ShapeAlgorithm';
import { ShapeKind, ShapeStyle } from './ShapeEnums';

// Define shape collections
const shapes = [
    ShapeKind.rectangle,
    ShapeKind.oval,
    ShapeKind.triangle,
    ShapeKind.rightangle,
    ShapeKind.rhombus,
    ShapeKind.pentagon,
    ShapeKind.hexagon,
    ShapeKind.octagon,
    ShapeKind.paragram,
    ShapeKind.heart,
    ShapeKind.quote,
    ShapeKind.trapezoid,
];

const banners = [
    // ShapeKind.rectBubble,
    // ShapeKind.cloudBubble,
    // ShapeKind.angryBubble,
    ShapeKind.banner3,
    ShapeKind.banner4,
    ShapeKind.banner1,
    ShapeKind.banner5,
    ShapeKind.banner6,
    ShapeKind.banner2,
];

const stars = [
    3, 4, 5, 6, 8, 12
];

const badges = [
    3, 4, 5, 6, 8, 12
];

const arrows = [
    ShapeKind.arrow1,
    ShapeKind.arrow2,
    ShapeKind.arrow3,
    ShapeKind.arrow4,
];

const useForceStrokeIfApplicable = (style: ShapeStyle, color: string) => {
    if (style === ShapeStyle.dash) return null;
    if (style === ShapeStyle.outline) return null;
    if (!Colors.isLight(color, style === ShapeStyle.fill ? 2 : 5)) return null;
    return { color: '#e6e6e6', width: 10 };
};

const modifyStrokeToPreventGhosting = (style: ShapeStyle, color: string, paths: ShapePath[]) => {
    if (style === ShapeStyle.dash) return paths;
    if (style === ShapeStyle.outline) return paths;
    if (!Colors.isLight(color, style === ShapeStyle.fill ? 2 : 5)) return paths;
    return paths.map(x => ({ ...x, stroke: '#e6e6e6', strokeWidth: 10 }));
};

const applyCheckboxToStrokeStyle = (style: ShapeStyle, color: string, paths: ShapePath[]) => {
    if (style !== ShapeStyle.stroke) return paths;
    return paths.map(x => ({ ...x, fill: 'url(#checkerboard)' }));
};

const useModifiedPaths = (style: ShapeStyle, color: string, paths: ShapePath[]) => {
    return useMemo(() => {
        // If only we had |> operator...
        let p = paths;
        p = modifyStrokeToPreventGhosting(style, color, p);
        p = applyCheckboxToStrokeStyle(style, color, p);
        return p;
    }, [paths]);
};

const ShapePreview = React.memo<{ kind: ShapeKind, style: ShapeStyle, color: string, sides: number }>((props) => {

    const { kind, style, color, sides } = props;
    const [size, setSize] = useState(Point.zero);

    const alg = ShapeAlgorithm.for(kind);

    const data = alg({
        color,
        cornerRadius: 6,
        stroke: 20,
        style,
        sides,
        width: size.w * 10,
        height: size.h * 10,
        dash: style === ShapeStyle.dash ? [20, 20] : undefined,
    });

    const modPaths = useModifiedPaths(style, color, data.paths);

    const svgStyle = {
        width: size.w,
        height: size.h,
    };

    const elmt = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!elmt.current) return;
        setSize(new Point(
            elmt.current.clientWidth,
            elmt.current.clientHeight,
        ));
    }, [elmt.current]);

    return <Flexer absolute inset=".25" ref={elmt} >
        <svg viewBox={`0 0 ${size.w * 10} ${size.h * 10}`} style={svgStyle}>
            <defs>
                <pattern id="checkerboard" patternUnits="userSpaceOnUse" width="200" height="200">
                    <rect x="100" width="100" height="100" fill="#E0E3EA" fillOpacity="0.35" />
                    <rect y="100" width="100" height="100" fill="#E0E3EA" fillOpacity="0.35" />
                </pattern>
            </defs>
            <g>
                {modPaths.map(({ d, ...pathProps }, i) => <path key={i} d={d.toString()} {...pathProps} />)}
            </g>
        </svg>
    </Flexer>;
});

// Create a new ShapeItem component for ElementSelector2
const ShapeItem = React.memo<{
    kind: ShapeKind,
    style: ShapeStyle,
    color: string,
    sides?: number,
    span?: number,
    onAction: (selectMode: 'click' | 'drag') => void,
}>(({ kind, style, color, sides = 4, span = 1, onAction }) => {
    const label = `${Enum.label(ShapeKind, kind)}${sides !== 4 ? `/${sides}` : ''}`;
    return <ElementSelector2.Item relative flex gridColumn={`span ${span}`} onAction={onAction}>
        <ShapePreview kind={kind} style={style} color={color} sides={sides} />
    </ElementSelector2.Item>;
});

const ItemGrid = styled.div<{ compact?: boolean }>`
    display: grid;
    grid-template-columns: repeat(6, ${p => p.compact ? '2.5rem' : '3.5rem'});
    grid-auto-rows: ${p => p.compact ? '2.5rem' : '3.5rem'};
    gap: 0.25rem;
    width: 100%;
`;

export interface ShapeSelectorProps {
    compact?: boolean;
    initialStyle?: ShapeStyle;
    color: string;
    onSelect: (kind: ShapeKind, sides: number, width: number, height: number, style: ShapeStyle) => (selectMode: 'click' | 'drag') => void;
}

const shapeStyleIcons = {
    [ShapeStyle.fillWhite]: 'deqo://icons/d2/shape-white',
    [ShapeStyle.fill]: 'deqo://icons/d2/shape-fill',
    [ShapeStyle.outline]: 'deqo://icons/d2/shape-border',
    [ShapeStyle.dash]: 'deqo://icons/d2/shape-dashed',
    [ShapeStyle.stroke]: 'deqo://icons/d2/shape-no-fill',
};

const shapeStyles = [
    ShapeStyle.fillWhite,
    ShapeStyle.fill,
    ShapeStyle.outline,
    ShapeStyle.dash,
    ShapeStyle.stroke,
];

export const ShapeSelector = React.memo<ShapeSelectorProps>(({ initialStyle, compact, color, onSelect }) => {
    const [style, setStyle] = useState(initialStyle ?? ShapeStyle.fillWhite);

    return <ElementSelector2 name="shapes"
        initialTab={`style-${initialStyle ?? ShapeStyle.fillWhite}`}
        contentWidth="22.25"
        height="35"
        background="rgba(245,245,245,0.5)">
        <ElementSelector2.Menu>
            {shapeStyles.map(shapeStyle => (
                <ElementSelector2.MenuItem
                    key={shapeStyle}
                    anchor={`style-${shapeStyle}`}
                    label={Enum.label(ShapeStyle, shapeStyle)}
                    icon={shapeStyleIcons[shapeStyle]}
                    iconSize="1.75"
                    onSelect={() => setStyle(shapeStyle)}
                />
            ))}
        </ElementSelector2.Menu>
        <Flexer height="1" />
        <ElementSelector2.Content >
            <ItemGrid compact={compact}>
                {shapes.map(kind => (
                    <ShapeItem
                        key={kind}
                        kind={kind}
                        style={style}
                        color={color}
                        onAction={onSelect(kind, 4, 1, 1, style)}
                    />
                ))}
                {stars.map(sides => (
                    <ShapeItem
                        key={`star-${sides}`}
                        kind={ShapeKind.star}
                        style={style}
                        color={color}
                        sides={sides}
                        onAction={onSelect(ShapeKind.star, sides, 1, 1, style)}
                    />
                ))}
                {badges.map(sides => (
                    <ShapeItem
                        key={`badge-${sides}`}
                        kind={ShapeKind.badge}
                        style={style}
                        color={color}
                        sides={sides}
                        onAction={onSelect(ShapeKind.badge, sides, 1, 1, style)}
                    />
                ))}
                {banners.map(kind => (
                    <ShapeItem
                        key={kind}
                        span={2}
                        kind={kind}
                        style={style}
                        color={color}
                        onAction={onSelect(kind, 4, 2, 1, style)}
                    />
                ))}
                {arrows.map(kind => (
                    <ShapeItem
                        key={kind}
                        span={2}
                        kind={kind}
                        style={style}
                        color={color}
                        onAction={onSelect(kind, 0, 2, 1, style)}
                    />
                ))}
            </ItemGrid>
        </ElementSelector2.Content>
    </ElementSelector2>;
});
